import { useEffect } from "react";
import { useSiteInfo } from "./use-site-info";

interface HSL {
  h: number;
  s: number;
  l: number;
}

const hexToHsl = (hex?: string): HSL | null => {
  if (!hex) return null;
  const cleanHex = hex.replace("#", "");
  if (cleanHex.length !== 6) return null;
  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
};

const hslToString = (hsl: HSL): string => {
  return `${hsl.h} ${hsl.s}% ${hsl.l}%`;
};

// Generate Tailwind-style shade palette (50-950)
// Uses the base color's lightness as the 500 shade and scales from there
const generateShadePalette = (baseHsl: HSL): Record<string, string> => {
  // Target lightness values for each shade (relative scale)
  // 500 = base color, lighter shades above, darker below
  const shadeTargets: Record<number, number> = {
    50: 97,
    100: 94,
    200: 86,
    300: 76,
    400: 62,
    500: 50, // Reference point
    600: 42,
    700: 34,
    800: 26,
    900: 20,
    950: 12,
  };

  const palette: Record<string, string> = {};
  const baseLightness = baseHsl.l;

  for (const [shade, targetLightness] of Object.entries(shadeTargets)) {
    const numShade = Number(shade);

    // Calculate lightness relative to the base color
    // Scale the difference from the 500 target (50%) proportionally
    let lightness: number;
    if (numShade === 500) {
      lightness = baseLightness;
    } else if (numShade < 500) {
      // Lighter shades: interpolate between base and target light values
      const ratio = (targetLightness - 50) / (97 - 50); // How far toward white
      lightness = baseLightness + ratio * (97 - baseLightness);
    } else {
      // Darker shades: interpolate between base and target dark values
      const ratio = (50 - targetLightness) / (50 - 12); // How far toward black
      lightness = baseLightness - ratio * (baseLightness - 12);
    }

    // Clamp lightness to valid range
    lightness = Math.max(5, Math.min(98, Math.round(lightness)));

    // Adjust saturation slightly for very light/dark shades
    let adjustedSaturation = baseHsl.s;
    if (numShade <= 100) {
      adjustedSaturation = Math.max(baseHsl.s - 20, 10);
    } else if (numShade >= 900) {
      adjustedSaturation = Math.min(baseHsl.s + 10, 100);
    }

    palette[shade] = hslToString({
      h: baseHsl.h,
      s: adjustedSaturation,
      l: lightness,
    });
  }

  return palette;
};

export function useSiteTheme() {
  const { data: siteInfo } = useSiteInfo();

  useEffect(() => {
    if (!siteInfo) return;
    const root = document.documentElement;

    const primaryHsl = hexToHsl(siteInfo.SitePrimaryColor);
    const secondaryHsl = hexToHsl(siteInfo.SiteSecondaryColor);

    if (primaryHsl) {
      // Set base primary (for backwards compatibility)
      root.style.setProperty("--primary", hslToString(primaryHsl));

      // Generate and set brand-primary shade palette
      const primaryPalette = generateShadePalette(primaryHsl);
      for (const [shade, value] of Object.entries(primaryPalette)) {
        root.style.setProperty(`--brand-primary-${shade}`, value);
      }
    }

    if (secondaryHsl) {
      // Set base secondary (for backwards compatibility)
      root.style.setProperty("--secondary", hslToString(secondaryHsl));

      // Generate and set brand-secondary shade palette
      const secondaryPalette = generateShadePalette(secondaryHsl);
      for (const [shade, value] of Object.entries(secondaryPalette)) {
        root.style.setProperty(`--brand-secondary-${shade}`, value);
      }
    }
  }, [siteInfo]);

  return siteInfo;
}
